import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PortalUpload } from "@/components/PortalUpload";
import { Building2, BedDouble, Bath, Ruler, MapPin, Home, CheckCircle2, Circle, FileText } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-sm text-[#252221]">
    <Icon size={15} className="text-[#1C4358]" />
    <span className="font-medium tabular-nums">{value ?? "—"}</span>
    <span className="text-muted-foreground text-xs">{label}</span>
  </div>
);

export default function PurchaserOpportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/portal/opportunities"); setItems(data.items || []); }
    catch (e) { if (e?.response?.status === 403) setGated(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try { const { data } = await api.get(`/portal/opportunities/${id}`); setDetail(data); }
    catch { toast.error("Could not load opportunity"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  if (gated) {
    return (
      <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="purchaser-gated">
        <Home size={26} className="mx-auto text-[#B7791F] mb-3" />
        <p className="font-medium text-[#252221]">Access pending approval</p>
        <p className="text-sm text-muted-foreground">Opportunities unlock once your investor agreement is approved.</p>
      </Card>
    );
  }

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#252221] mb-1">Property Opportunities</h2>
      <p className="text-sm text-muted-foreground mb-5">Approved property opportunities shared with you by MovingMyMama.</p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Skeleton className="h-44" /><Skeleton className="h-44" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="purchaser-empty">
          <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><Home size={26} className="text-[#1C4358]" /></div>
          <p className="font-medium text-[#252221]">No opportunities yet</p>
          <p className="text-sm text-muted-foreground">When a property is approved for investors, it will appear here.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="p-5 border-[#E6EDF2] cursor-pointer hover:bg-[rgba(155,185,206,0.06)] transition-colors" style={{ boxShadow: "var(--mmm-shadow-sm)" }} onClick={() => openDetail(p.id)} data-testid="purchaser-opportunity-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}><Building2 size={20} className="text-[#1C4358]" /></div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[#252221] truncate">{p.address || "Property"}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} /> {[p.city, p.state, p.zip].filter(Boolean).join(", ") || "—"}</div>
                  </div>
                </div>
                {p.status && <StatusBadge value={p.status} />}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
                <Stat icon={BedDouble} label="beds" value={p.beds} />
                <Stat icon={Bath} label="baths" value={p.baths} />
                <Stat icon={Ruler} label="sqft" value={p.sqft} />
              </div>
              <div className="flex items-center justify-between border-t border-[#F0F4F7] pt-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">List price</div>
                  <div className="text-lg font-bold font-display tabular-nums text-[#252221]">{p.list_price ? formatCurrency(p.list_price) : "—"}</div>
                </div>
                <span className="text-xs text-[#1C4358] font-medium">View details</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto mmm-scroll" data-testid="purchaser-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.address || "Opportunity"}</DialogTitle></DialogHeader>
          {detailLoading ? <Skeleton className="h-40" /> : detail && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {[detail.city, detail.state, detail.zip].filter(Boolean).join(", ")}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                <Stat icon={BedDouble} label="beds" value={detail.beds} />
                <Stat icon={Bath} label="baths" value={detail.baths} />
                <Stat icon={Ruler} label="sqft" value={detail.sqft} />
                <Stat icon={Home} label={prettify(detail.property_type || "")} value={""} />
              </div>
              <div className="rounded-xl bg-[#F7FAFC] p-4">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">List price</div>
                <div className="text-2xl font-bold font-display tabular-nums text-[#252221]">{detail.list_price ? formatCurrency(detail.list_price) : "—"}</div>
              </div>
              {detail.purchaser_notes && <div><div className="text-sm font-semibold text-[#252221] mb-1">Notes</div><p className="text-sm text-muted-foreground leading-relaxed">{detail.purchaser_notes}</p></div>}
              {detail.seller_timeline?.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-[#252221] mb-2">Seller timeline</div>
                  <ul className="space-y-1.5">
                    {detail.seller_timeline.map((t, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">{t.done ? <CheckCircle2 size={16} className="text-[#2F7D5A]" /> : <Circle size={16} className="text-[#9BB9CE]" />}<span className={t.done ? "text-muted-foreground" : "text-[#252221]"}>{t.label}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {detail.documents?.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-[#252221] mb-2">Shared documents</div>
                  {detail.documents.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm text-[#1C4358]"><FileText size={15} /> {d.original_filename}</div>
                  ))}
                </div>
              )}
              <div className="pt-2 border-t border-[#F0F4F7]">
                <div className="text-sm font-semibold text-[#252221] mb-2">Submit an offer</div>
                <PortalUpload endpoint="/portal/purchaser/documents/upload" fields={{ property_id: detail.id }} label="Upload offer document" testid="purchaser-offer-upload" onUploaded={() => openDetail(detail.id)} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
